// Imports
import apiActions from '../../../shareables/integrations/api/redux/actions';
import { GetWeatherResponse } from '../../../shareables/types/api/main/website/homepage/weather/get';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
	faCloudBolt,
	faCloudFog,
	faCloudMoon,
	faCloudMoonRain,
	faCloudRain,
	faClouds,
	faCloudSun,
	faCloudSunRain,
	faMoonStars,
	faSnowflakes,
	faSun,
} from '@fortawesome/pro-regular-svg-icons';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import React, { useEffect, useState } from 'react';
import { useAppDispatch } from '../../../shareables/foundation/front-end/redux/hooks';
import styled from 'styled-components/macro';
import { GeneratedThemeProps } from '../../templates/template-style';


// Map icon response to Font Awesome
const icons: Record<GetWeatherResponse['descriptor'], IconDefinition> = {
	'clear-sky-day': faSun,
	'few-clouds-day': faCloudSun,
	'scattered-clouds-day': faClouds,
	'broken-clouds-day': faClouds,
	'shower-rain-day': faCloudRain,
	'rain-day': faCloudSunRain,
	'thunderstorm-day': faCloudBolt,
	'snow-day': faSnowflakes,
	'mist-day': faCloudFog,
	'clear-sky-night': faMoonStars,
	'few-clouds-night': faCloudMoon,
	'scattered-clouds-night': faClouds,
	'broken-clouds-night': faClouds,
	'shower-rain-night': faCloudRain,
	'rain-night': faCloudMoonRain,
	'thunderstorm-night': faCloudBolt,
	'snow-night': faSnowflakes,
	'mist-night': faCloudFog,
};

const WeatherContainer = styled.div<{ theme: GeneratedThemeProps }>`
	display: flex;
	color: #333;
	
	padding: 0.625rem;
	background: rgba(255, 255, 255, 0.9);
	position: absolute;
	top: 0.625rem;
	right: 0.625rem;
	
	border-radius: ${(props) => (props.theme.isPaper ? 'none' : '0.3125rem')};
	line-height: 1.42857143;
	
	@media only screen and (max-width: 48rem) {
		display: none;
	}
`;

const WeatherIcon = styled.div`
	padding-right: 0.625rem;
	font-size: 1.875rem;
`;

const WeatherCondition = styled.div`
	font-size: 1rem;
`;

const WeatherDetails = styled.div`
	padding-top: 0.0938rem;
	font-size: 0.8125rem;
	font-style: italic;
`;


// Define the accepted props
interface Props {
	storybookWeather?: GetWeatherResponse;
	storybookWeatherError?: string;
}


// Function component
const Weather: React.FC<Props> = ({ storybookWeather, storybookWeatherError }) => {
	// Use state
	const [weather, setWeather] = useState<GetWeatherResponse | null>(storybookWeather ?? null);
	const [weatherError, setWeatherError] = useState<string | null>(storybookWeatherError ?? null);
	
	
	// Use Redux functionality
	const dispatch = useAppDispatch();
	
	
	// Load weather info on mount
	useEffect(() => {
		// Get weather
		void dispatch(
			apiActions.call<undefined, undefined, GetWeatherResponse>({
				action: 'GET',
				uri: `/website/${window.location.hostname}/homepage/weather`,
				handleErrorsAutomatically: false,
				completion: (json) => {
					// Check for response errors
					if (json.status === 'error') {
						// Handle expected error messages
						if (json.message.toLowerCase().includes('location not found.')) {
							setWeatherError('Weather: Location not found');
							return;
						}
						
						if (json.message.toLowerCase().includes('invalid alternate location for weather.')) {
							setWeatherError('Invalid alternate location for weather');
							return;
						}
						
						
						// Handle all other errors
						setWeatherError('Error loading weather');
						return;
					}
					
					
					// Update state
					setWeather(json.response);
				},
			})
		);
	}, [dispatch]);
	
	return (
		<WeatherContainer>
			{weatherError ? (
				<WeatherCondition>{weatherError}</WeatherCondition>
			) : (
				weather && (
					<React.Fragment>
						<WeatherIcon>
							<FontAwesomeIcon icon={icons[weather.descriptor]} />
						</WeatherIcon>
						<div>
							<WeatherCondition>{weather.condition}</WeatherCondition>
							<WeatherDetails>
								{weather.temperature}&deg;{weather.scale} | {weather.humidity}% Humidity
							</WeatherDetails>
						</div>
					</React.Fragment>
				)
			)}
		</WeatherContainer>
	);
};

export default Weather;
